import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSecureData } from '../hooks/useSecureData';
import TherapyNoteForm from '../components/TherapyNoteForm';
import TherapyNotesList from '../components/TherapyNotesList';
import TreatmentPlanList from '../components/TreatmentPlanList';
import secureApiClient from '../utils/secureApiClient';
import { db } from '../firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';

const Clients = () => {
  const location = useLocation();
  const { isAuthenticated, canPerform } = useSecureData();

  const [clients, setClients] = useState([]);
  const [therapistList, setTherapistList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('data');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [notification, setNotification] = useState(null);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [notesRefresh, setNotesRefresh] = useState(0);
  const [assigning, setAssigning] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [assessment, setAssessment] = useState(null);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [clientNotes, setClientNotes] = useState([]);
  const [clientNotesLoading, setClientNotesLoading] = useState(false);

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const showNotification = (message, type = 'success') => setNotification({ message, type });

  // Fetch all clients from Firestore via getPortalClients
  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      setError('Authentication required');
      return;
    }
    if (!canPerform('view_clients')) {
      setLoading(false);
      setError('Insufficient permissions to view clients');
      return;
    }
    setLoading(true);
    setError(null);
    secureApiClient.makeSecureRequest(
      secureApiClient.baseURLs.cloudFunctions.getPortalClients,
      { method: 'GET' }
    )
      .then(data => {
        setClients(data.clients || []);
        setTherapistList(data.therapists || []);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [isAuthenticated, canPerform]);

  // Handle client pre-selected from navigation state (e.g. from Associates page)
  useEffect(() => {
    if (location.state?.selectedClient) {
      setSelectedClient(location.state.selectedClient);
    }
  }, [location.state]);

  // Load self-assessment for selected client (real-time)
  useEffect(() => {
    if (!selectedClient?.clinicalId) { setAssessment(null); return; }
    setAssessmentLoading(true);
    const unsub = onSnapshot(
      collection(db, 'clinicalRecords', selectedClient.clinicalId, 'assessments'),
      snap => {
        const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Sort by analysisTimestamp descending, take the latest
        docs.sort((a, b) => {
          const ta = a.analysisTimestamp?.toDate?.() || new Date(a.analysisTimestamp || 0);
          const tb = b.analysisTimestamp?.toDate?.() || new Date(b.analysisTimestamp || 0);
          return tb - ta;
        });
        setAssessment(docs.length > 0 ? docs[0] : null);
        setAssessmentLoading(false);
      },
      () => { setAssessment(null); setAssessmentLoading(false); }
    );
    return () => unsub();
  }, [selectedClient?.clinicalId]);

  // Load client journal entries for selected client (real-time, read-only for staff)
  useEffect(() => {
    if (!selectedClient?.clinicalId) { setClientNotes([]); return; }
    setClientNotesLoading(true);
    const q = query(
      collection(db, 'clinicalRecords', selectedClient.clinicalId, 'journal'),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(
      q,
      snap => {
        setClientNotes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        setClientNotesLoading(false);
      },
      () => { setClientNotes([]); setClientNotesLoading(false); }
    );
    return () => unsub();
  }, [selectedClient?.clinicalId]);

  const handleStatusChange = async (clinicalId, status) => {
    if (!canPerform('edit_clients')) {
      showNotification('Insufficient permissions to update client status', 'error');
      return;
    }
    const prevClients = clients;
    // Optimistic update
    setClients(prev => prev.map(c => c.clinicalId === clinicalId ? { ...c, status } : c));
    if (selectedClient?.clinicalId === clinicalId) {
      setSelectedClient(prev => ({ ...prev, status }));
    }
    try {
      await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.updatePortalClientStatus,
        { method: 'POST', body: JSON.stringify({ clinicalId, status }) }
      );
      const c = prevClients.find(c => c.clinicalId === clinicalId);
      showNotification(`${c ? `${c.firstName} ${c.lastName}` : 'Client'} status updated to ${status}`, 'success');
    } catch (err) {
      // Revert on failure
      setClients(prevClients);
      if (selectedClient?.clinicalId === clinicalId) {
        const original = prevClients.find(c => c.clinicalId === clinicalId);
        if (original) setSelectedClient(original);
      }
      showNotification('Failed to update client status', 'error');
    }
  };

  const handleAssignTherapist = async (clinicalId, therapistUid) => {
    const therapist = therapistList.find(t => t.uid === therapistUid);
    setAssigning(clinicalId);
    try {
      await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.assignPortalClientTherapist,
        {
          method: 'POST',
          body: JSON.stringify({ clinicalId, therapistUid, therapistName: therapist?.name || '' })
        }
      );
      const updates = {
        assignedTherapistId: therapistUid,
        assignedTherapistName: therapist?.name || '',
        status: 'active'
      };
      setClients(prev => prev.map(c => c.clinicalId === clinicalId ? { ...c, ...updates } : c));
      if (selectedClient?.clinicalId === clinicalId) {
        setSelectedClient(prev => ({ ...prev, ...updates }));
      }
      showNotification('Therapist assigned successfully', 'success');
    } catch (err) {
      showNotification('Failed to assign therapist', 'error');
    } finally {
      setAssigning(null);
    }
  };

  const handleEditStart = () => {
    setEditForm({
      preferredName:        selectedClient.preferredName || '',
      phone:                selectedClient.phone || '',
      dateOfBirth:          selectedClient.dateOfBirth || '',
      street:               selectedClient.street || '',
      city:                 selectedClient.city || '',
      state:                selectedClient.state || '',
      zip:                  selectedClient.zip || '',
      emergencyContactName: selectedClient.emergencyContactName || '',
      emergencyContactPhone:selectedClient.emergencyContactPhone || '',
      gender:               selectedClient.gender || '',
      maritalStatus:        selectedClient.maritalStatus || '',
      employmentStatus:     selectedClient.employmentStatus || '',
      referralSource:       selectedClient.referralSource || '',
    });
    setEditMode(true);
  };

  const handleEditCancel = () => { setEditMode(false); setEditForm({}); };

  const handleEditSave = async () => {
    setEditSaving(true);
    try {
      const result = await secureApiClient.makeSecureRequest(
        secureApiClient.baseURLs.cloudFunctions.submitClientIntake,
        { method: 'POST', body: JSON.stringify({ ...editForm, targetUid: selectedClient.uid }) }
      );
      const updated = { ...selectedClient, ...editForm, intakeCompleted: result.intakeCompleted, intakeSubmittedAt: result.intakeSubmittedAt };
      setClients(prev => prev.map(c => c.uid === selectedClient.uid ? updated : c));
      setSelectedClient(updated);
      setEditMode(false);
      setEditForm({});
      showNotification('Client information updated successfully');
    } catch (err) {
      showNotification('Failed to save changes', 'error');
    } finally {
      setEditSaving(false);
    }
  };

  const filteredClients = clients.filter(c => {
    const fullName = `${c.firstName} ${c.lastName}`.toLowerCase();
    const matchesSearch = fullName.includes(searchTerm.toLowerCase());
    const isActive = c.status !== 'inactive';
    return matchesSearch && (showActiveOnly ? isActive : !isActive);
  });

  if (loading) return <div className="flex"><div className="flex-1 p-8">Loading...</div></div>;
  if (error) return <div className="flex"><div className="flex-1 p-8 text-red-600">Error: {error}</div></div>;

  return (
    <div className="flex">
      <div className="flex-1 p-8">

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 z-50 max-w-sm">
            <div className={`rounded-lg shadow-lg p-4 ${
              notification.type === 'success' ? 'bg-green-500 text-white' :
              notification.type === 'error'   ? 'bg-red-500 text-white' :
              'bg-blue-500 text-white'
            }`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{notification.message}</p>
                <button onClick={() => setNotification(null)} className="ml-4 text-white hover:text-gray-200">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="container mx-auto">

          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold">Clients</h1>
              <p className="text-gray-600 mt-1">
                {filteredClients.length} {showActiveOnly ? 'active / pending' : 'inactive'} clients
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <input
                type="text"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-64 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium">Show:</span>
                <button
                  onClick={() => setShowActiveOnly(true)}
                  className={`px-3 py-1 rounded text-sm font-medium ${showActiveOnly ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >Active / Pending</button>
                <button
                  onClick={() => setShowActiveOnly(false)}
                  className={`px-3 py-1 rounded text-sm font-medium ${!showActiveOnly ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >Inactive</button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

            {/* Client List */}
            <div className="md:col-span-1 bg-white rounded-lg shadow p-4">
              <h2 className="text-xl font-semibold mb-4">
                Clients List
                <span className="text-sm font-normal text-gray-500 ml-2">({filteredClients.length})</span>
              </h2>
              <div className="space-y-2">
                {filteredClients.length === 0 ? (
                  <div className="text-gray-500 text-center py-4 text-sm">
                    {clients.length === 0
                      ? 'No clients found'
                      : `No ${showActiveOnly ? 'active / pending' : 'inactive'} clients`}
                  </div>
                ) : (
                  filteredClients.map(c => (
                    <div
                      key={c.uid}
                      className={`p-3 rounded cursor-pointer ${selectedClient?.uid === c.uid ? 'bg-brand-100' : 'hover:bg-gray-100'}`}
                      onClick={() => { setSelectedClient(c); setActiveTab('data'); setShowNoteForm(false); setEditMode(false); setEditForm({}); }}
                    >
                      <div className="font-medium">{c.firstName} {c.lastName}</div>
                      <div className="text-sm text-gray-500">
                        {c.insuranceProvider || c.insuranceType || 'No insurance on file'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {c.assignedTherapistName || 'Unassigned'}
                      </div>
                      <div className={`text-sm mt-0.5 ${c.status === 'active' ? 'text-green-600' : 'text-amber-600'}`}>
                        {c.status === 'active' ? 'Active' : c.status === 'pending' ? 'Pending' : 'Inactive'}
                      </div>
                      <div className={`text-xs font-medium mt-1 ${c.intakeCompleted ? 'text-green-700' : 'text-amber-700'}`}>
                        {c.intakeCompleted ? 'Intake complete' : 'Intake incomplete'}
                      </div>
                      {!c.assignedTherapistId && (
                        <div className="text-xs text-amber-600 font-medium mt-0.5">⚠ Unassigned</div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Client Detail Panel */}
            {selectedClient && (
              <div className="md:col-span-2 bg-white rounded-lg shadow p-4">

                {/* Detail Header */}
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-xl font-semibold">
                      {selectedClient.firstName} {selectedClient.lastName}
                      {selectedClient.preferredName && selectedClient.preferredName !== selectedClient.firstName && (
                        <span className="text-base font-normal text-gray-500 ml-2">
                          ({selectedClient.preferredName})
                        </span>
                      )}
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        selectedClient.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : selectedClient.status === 'pending'
                          ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {selectedClient.status === 'active' ? 'Active' : selectedClient.status === 'pending' ? 'Pending' : 'Inactive'}
                      </span>
                      {selectedClient.assignedTherapistName && (
                        <span className="text-xs text-gray-500">
                          Therapist: {selectedClient.assignedTherapistName}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-3 flex-wrap justify-end">
                    {/* Status change */}
                    {canPerform('edit_clients') && (
                      <select
                        value={selectedClient.status === 'active' ? 'active' : 'inactive'}
                        onChange={e => handleStatusChange(selectedClient.clinicalId, e.target.value)}
                        className="px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    )}

                    {/* Therapist assignment */}
                    {canPerform('assign_therapists') && (
                      <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-600 font-medium">Assign:</label>
                        <select
                          value={selectedClient.assignedTherapistId || ''}
                          disabled={assigning === selectedClient.clinicalId}
                          onChange={e => {
                            if (!e.target.value) return;
                            handleAssignTherapist(selectedClient.clinicalId, e.target.value);
                          }}
                          className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                        >
                          <option value="">— Select therapist —</option>
                          {therapistList.map(t => (
                            <option key={t.uid} value={t.uid}>{t.name}</option>
                          ))}
                        </select>
                        {assigning === selectedClient.clinicalId && (
                          <span className="text-xs text-gray-400">Saving…</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {!selectedClient.intakeCompleted && (
                  <div role="status" className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                    Intake incomplete. Required questionnaire answers or consents are missing.
                    Saved answers are available below; ask the client to finish Client Intake in their portal.
                  </div>
                )}

                {/* Tabs */}
                <div className="border-b border-gray-200 mb-6">
                  <nav className="-mb-px flex space-x-8">
                    {[
                      { key: 'data',           label: 'Client Information' },
                      { key: 'insurance',      label: 'Insurance & Billing' },
                      { key: 'clinical',       label: 'Clinical' },
                      { key: 'assessments',    label: 'Assessments' },
                      { key: 'notes',          label: 'Therapy Notes' },
                      { key: 'treatment-plan', label: 'Therapy Plan' },
                      { key: 'client-notes',   label: 'Client Notes' },
                    ].map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                          activeTab === tab.key
                            ? 'border-brand-500 text-brand-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >{tab.label}</button>
                    ))}
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="space-y-6">

                  {/* ── Client Information ── */}
                  {activeTab === 'data' && (
                    <section>

                      {/* Edit / Save / Cancel controls — admin only */}
                      {canPerform('edit_clients') && (
                        <div className="flex justify-end mb-4">
                          {editMode ? (
                            <div className="flex gap-2">
                              <button
                                onClick={handleEditCancel}
                                className="px-4 py-1.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-600 hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleEditSave}
                                disabled={editSaving}
                                className="px-4 py-1.5 rounded-lg text-sm font-medium text-white disabled:opacity-60"
                                style={{ backgroundColor: '#1e562d' }}
                              >
                                {editSaving ? 'Saving…' : 'Save Changes'}
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={handleEditStart}
                              className="px-4 py-1.5 rounded-lg text-sm font-medium border text-white"
                              style={{ backgroundColor: '#1e562d' }}
                            >
                              Edit
                            </button>
                          )}
                        </div>
                      )}

                      <h3 className="text-lg font-medium mb-2">Basic Information</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-500">Legal First Name</label>
                          <div className="mt-0.5">{selectedClient.firstName || '—'}</div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Legal Last Name</label>
                          <div className="mt-0.5">{selectedClient.lastName || '—'}</div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Preferred Name</label>
                          {editMode ? (
                            <input className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.preferredName} onChange={e => setEditForm(f => ({ ...f, preferredName: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.preferredName || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Date of Birth</label>
                          {editMode ? (
                            <input type="date" className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.dateOfBirth} onChange={e => setEditForm(f => ({ ...f, dateOfBirth: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.dateOfBirth || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Email</label>
                          <div className="mt-0.5">{selectedClient.email || '—'}</div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Phone</label>
                          {editMode ? (
                            <input type="tel" className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.phone} onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.phone || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Gender / Pronouns</label>
                          {editMode ? (
                            <select className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={editForm.gender} onChange={e => setEditForm(f => ({ ...f, gender: e.target.value }))}>
                              <option value="">Select…</option>
                              {['He/Him','She/Her','They/Them','Other'].map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <div className="mt-0.5">{selectedClient.gender || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Marital Status</label>
                          {editMode ? (
                            <select className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={editForm.maritalStatus} onChange={e => setEditForm(f => ({ ...f, maritalStatus: e.target.value }))}>
                              <option value="">Select…</option>
                              {['Married','Single','Divorced','Separated','Widowed','Other'].map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <div className="mt-0.5">{selectedClient.maritalStatus || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Employment Status</label>
                          {editMode ? (
                            <select className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={editForm.employmentStatus} onChange={e => setEditForm(f => ({ ...f, employmentStatus: e.target.value }))}>
                              <option value="">Select…</option>
                              {['Full Time','Part Time','Unemployed','Self-Employed','Attending School','Other'].map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <div className="mt-0.5">{selectedClient.employmentStatus || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Referral Source</label>
                          {editMode ? (
                            <select className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm bg-white" value={editForm.referralSource} onChange={e => setEditForm(f => ({ ...f, referralSource: e.target.value }))}>
                              <option value="">Select…</option>
                              {['Insurance Website','Psychology Today','Personal Referral','shanebruce.com','Other'].map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <div className="mt-0.5">{selectedClient.referralSource || '—'}</div>
                          )}
                        </div>
                      </div>

                      <h3 className="text-lg font-medium mb-2 mt-6">Address</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <label className="text-sm text-gray-500">Street Address</label>
                          {editMode ? (
                            <input className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.street} onChange={e => setEditForm(f => ({ ...f, street: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.street || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">City</label>
                          {editMode ? (
                            <input className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.city} onChange={e => setEditForm(f => ({ ...f, city: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.city || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">State</label>
                          {editMode ? (
                            <input maxLength={2} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.state} onChange={e => setEditForm(f => ({ ...f, state: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.state || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Zip Code</label>
                          {editMode ? (
                            <input maxLength={10} className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.zip} onChange={e => setEditForm(f => ({ ...f, zip: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.zip || '—'}</div>
                          )}
                        </div>
                      </div>

                      <h3 className="text-lg font-medium mb-2 mt-6">Emergency Contact</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-500">Name</label>
                          {editMode ? (
                            <input className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.emergencyContactName} onChange={e => setEditForm(f => ({ ...f, emergencyContactName: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.emergencyContactName || '—'}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Phone</label>
                          {editMode ? (
                            <input type="tel" className="mt-0.5 w-full border border-gray-300 rounded px-2 py-1 text-sm" value={editForm.emergencyContactPhone} onChange={e => setEditForm(f => ({ ...f, emergencyContactPhone: e.target.value }))} />
                          ) : (
                            <div className="mt-0.5">{selectedClient.emergencyContactPhone || '—'}</div>
                          )}
                        </div>
                      </div>
                    </section>
                  )}

                  {/* ── Insurance & Billing ── */}
                  {activeTab === 'insurance' && (
                    <section>
                      <h3 className="text-lg font-medium mb-2">Payment</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-500">Payment Preference</label>
                          <div>{selectedClient.paymentPreference || '—'}</div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Copay</label>
                          <div>{selectedClient.copay || '—'}</div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Deductible</label>
                          <div>{selectedClient.deductible || '—'}</div>
                        </div>
                      </div>

                      <h3 className="text-lg font-medium mb-2 mt-6">Insurance</h3>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm text-gray-500">Provider</label>
                          <div>{selectedClient.insuranceProvider || selectedClient.insuranceType || '—'}</div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Plan Name</label>
                          <div>{selectedClient.insurancePlanName || '—'}</div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Member ID / Policy Number</label>
                          <div>{selectedClient.insuranceMemberId || '—'}</div>
                        </div>
                        <div>
                          <label className="text-sm text-gray-500">Group Number</label>
                          <div>{selectedClient.insuranceGroupId || '—'}</div>
                        </div>
                      </div>
                    </section>
                  )}

                  {/* ── Clinical ── */}
                  {activeTab === 'clinical' && (
                    <section>
                      <div className="mb-6 grid grid-cols-2 gap-4 text-sm">
                        {[
                          ['Employer / School', selectedClient.employerOrSchool],
                          ['Insurance Phone', selectedClient.insurancePhone],
                          ['Out-of-Pocket Maximum', selectedClient.oopMax],
                          ['Marital Status', selectedClient.maritalStatus],
                          ['Gender / Pronouns', selectedClient.gender],
                          ['Employment Status', selectedClient.employmentStatus],
                          ['Referral Source', selectedClient.referralSource],
                          ['Taking Medication', selectedClient.medicationsCurrent === true ? 'Yes' : selectedClient.medicationsCurrent === false ? 'No' : null],
                          ['Informed Consent', selectedClient.consentInformedConsent === true ? 'Accepted' : selectedClient.consentInformedConsent === false ? 'Not accepted' : 'Not recorded'],
                          ['Cancellation Policy', selectedClient.consentCancellationPolicy === true ? 'Accepted' : selectedClient.consentCancellationPolicy === false ? 'Not accepted' : 'Not recorded'],
                          ['Telehealth Consent', selectedClient.consentTelehealth === true ? 'Accepted' : selectedClient.consentTelehealth === false ? 'Not accepted' : 'Not recorded'],
                        ].map(([label, value]) => (
                          <div key={label}><div className="text-gray-500">{label}</div><div>{value || 'Not provided'}</div></div>
                        ))}
                      </div>

                      {selectedClient.reasonForReachingOut && (
                        <div className="mb-6">
                          <h3 className="text-lg font-medium mb-2">Reason for Reaching Out</h3>
                          <div className="p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400 text-sm whitespace-pre-wrap">
                            {selectedClient.reasonForReachingOut}
                          </div>
                        </div>
                      )}

                      {selectedClient.conditions?.length > 0 && (
                        <div className="mb-6">
                          <h3 className="text-lg font-medium mb-2">Reported Concerns</h3>
                          <div className="flex flex-wrap gap-2">
                            {selectedClient.conditions.map((cond, i) => (
                              <span key={i} className="inline-block bg-blue-100 rounded-full px-3 py-1 text-sm font-semibold text-blue-700">
                                {cond}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {selectedClient.medicationsCurrent && (
                        <div className="mb-6">
                          <h3 className="text-lg font-medium mb-2">Current Medications</h3>
                          <div className="p-3 bg-gray-50 rounded-lg text-sm">
                            {selectedClient.medicationsList || 'Yes (no details provided)'}
                          </div>
                        </div>
                      )}

                      {!selectedClient.reasonForReachingOut && !selectedClient.conditions?.length && selectedClient.medicationsCurrent == null && !selectedClient.maritalStatus && !selectedClient.gender && !selectedClient.employmentStatus && !selectedClient.referralSource && (
                        <div className="text-gray-500 text-center py-8 text-sm">
                          No clinical information on file
                        </div>
                      )}
                    </section>
                  )}

                  {/* ── Assessments ── */}
                  {activeTab === 'assessments' && (
                    <section>
                      {assessmentLoading ? (
                        <div className="text-gray-400 text-sm text-center py-8">Loading assessment…</div>
                      ) : !assessment ? (
                        <div className="text-center py-10">
                          <div className="text-gray-400 text-sm mb-2">No self-assessment on file</div>
                          <p className="text-xs text-gray-400">
                            The client needs to complete the self-assessment form before this section will populate.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-5">
                          {/* Status badge */}
                          <div className="flex items-center gap-2">
                            <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${assessment.analyzed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                              {assessment.analyzed ? 'AI Analysis Complete' : 'Pending Analysis'}
                            </span>
                            {assessment.analysisTimestamp && (
                              <span className="text-xs text-gray-400">
                                {(assessment.analysisTimestamp?.toDate?.() || new Date(assessment.analysisTimestamp)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                              </span>
                            )}
                          </div>

                          {/* Reported symptoms */}
                          {assessment.selectedCheckboxes?.length > 0 && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 mb-2">Reported Symptoms</h4>
                              <div className="flex flex-wrap gap-2">
                                {assessment.selectedCheckboxes.map((s, i) => (
                                  <span key={i} className="inline-block bg-blue-100 text-blue-700 rounded-full px-3 py-1 text-xs font-medium">{s}</span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Additional info from the form */}
                          {assessment.additionalInfo && (
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 mb-1">Client's Own Words</h4>
                              <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-wrap border-l-4 border-gray-300">
                                {assessment.additionalInfo}
                              </div>
                            </div>
                          )}

                          {/* AI Analysis */}
                          {assessment.aiAnalysis && (
                            <div className="space-y-3">
                              <h4 className="text-sm font-semibold text-gray-700">AI Clinical Summary</h4>
                              {assessment.aiAnalysis.summary && (
                                <div className="p-4 bg-brand-50 rounded-lg border-l-4 border-brand-400">
                                  <p className="text-xs font-semibold text-brand-600 mb-1 uppercase tracking-wide">Summary</p>
                                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{assessment.aiAnalysis.summary}</p>
                                </div>
                              )}
                              {assessment.aiAnalysis.suggestedPlan && (
                                <div className="p-4 bg-purple-50 rounded-lg border-l-4 border-purple-400">
                                  <p className="text-xs font-semibold text-purple-600 mb-1 uppercase tracking-wide">Suggested Approach</p>
                                  <p className="text-sm text-gray-800 whitespace-pre-wrap">{assessment.aiAnalysis.suggestedPlan}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </section>
                  )}

                  {/* ── Therapy Notes ── */}
                  {activeTab === 'notes' && (
                    <section>
                      {/* Client summary banner */}
                      <div className="mb-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                        <h4 className="text-sm font-semibold text-blue-800 mb-2">Client Summary</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-blue-600 font-medium">Date of Birth:</span>
                            <div className="text-gray-800">{selectedClient.dateOfBirth || 'Not on file'}</div>
                          </div>
                          <div>
                            <span className="text-blue-600 font-medium">Phone:</span>
                            <div className="text-gray-800">{selectedClient.phone || 'Not on file'}</div>
                          </div>
                          <div>
                            <span className="text-blue-600 font-medium">Emergency Contact:</span>
                            <div className="text-gray-800">{selectedClient.emergencyContactName || 'Not provided'}</div>
                          </div>
                          <div>
                            <span className="text-blue-600 font-medium">Assigned Therapist:</span>
                            <div className="text-gray-800">{selectedClient.assignedTherapistName || 'Unassigned'}</div>
                          </div>
                        </div>
                      </div>

                      {canPerform('create_notes') && (
                        <div className="flex justify-between items-center mb-4">
                          <h4 className="text-md font-medium">Progress Notes</h4>
                          <button
                            onClick={() => setShowNoteForm(!showNoteForm)}
                            className={`px-4 py-2 rounded focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                              showNoteForm
                                ? 'bg-gray-500 text-white hover:bg-gray-600'
                                : 'bg-brand-600 text-white hover:bg-brand-700'
                            }`}
                          >
                            {showNoteForm ? 'Cancel' : 'New Therapy Note'}
                          </button>
                        </div>
                      )}

                      {showNoteForm && canPerform('create_notes') && (
                        <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                          <TherapyNoteForm
                            clinicalId={selectedClient.clinicalId}
                            clientName={`${selectedClient.firstName} ${selectedClient.lastName}`}
                            clientData={selectedClient}
                            onClose={() => setShowNoteForm(false)}
                            onSaved={() => {
                              setShowNoteForm(false);
                              setNotesRefresh(prev => prev + 1);
                              showNotification('Therapy note saved successfully');
                            }}
                          />
                        </div>
                      )}

                      {canPerform('view_notes') ? (
                        <TherapyNotesList
                          clinicalId={selectedClient.clinicalId}
                          legacyClientId={selectedClient.legacyClientId}
                          key={notesRefresh}
                        />
                      ) : (
                        <div className="text-gray-500 text-center py-8 text-sm">
                          You don't have permission to view therapy notes
                        </div>
                      )}
                    </section>
                  )}

                  {/* ── Therapy Plan ── */}
                  {activeTab === 'treatment-plan' && (
                    <section>
                      {canPerform('view_notes') ? (
                        <TreatmentPlanList
                          clinicalId={selectedClient.clinicalId}
                          clientName={`${selectedClient.firstName} ${selectedClient.lastName}`}
                          clientData={selectedClient}
                        />
                      ) : (
                        <div className="text-gray-500 text-center py-8 text-sm">
                          You don't have permission to view therapy plans
                        </div>
                      )}
                    </section>
                  )}

                  {/* ── Client Notes (journal) ── */}
                  {activeTab === 'client-notes' && (
                    <section>
                      {clientNotesLoading ? (
                        <div className="text-gray-400 text-sm text-center py-8">Loading client notes…</div>
                      ) : clientNotes.length === 0 ? (
                        <div className="text-center py-10">
                          <div className="text-gray-400 text-sm mb-1">No journal entries yet</div>
                          <p className="text-xs text-gray-400">
                            The client hasn't written any notes. They can add entries from their portal.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <p className="text-xs text-gray-400 mb-2">{clientNotes.length} {clientNotes.length === 1 ? 'entry' : 'entries'} — read only</p>
                          {clientNotes.map(entry => {
                            const date = entry.createdAt?.toDate?.() || new Date(entry.createdAt || 0);
                            return (
                              <div key={entry.id} className="border border-gray-200 rounded-lg p-4">
                                <div className="text-xs text-gray-400 mb-2">
                                  {date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                  {' · '}
                                  {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                                </div>
                                <p className="text-sm text-gray-800 whitespace-pre-wrap">{entry.content}</p>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </section>
                  )}

                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Clients;
